import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type VideoClip = {
  question_id: string;
  question_text: string | null;
  video_url: string; // presigned GET for the ORIGINAL captured video
  video_mp4_url: string | null; // presigned GET for a future MP4 derivative, or null
  video_content_type: string | null; // stored original container: video/webm | video/mp4
};
type VideoSeamResponse = { enabled: boolean; clips: VideoClip[] };

type State =
  | { status: "loading" }
  | { status: "ready"; enabled: boolean; clips: VideoClip[] }
  | { status: "error" };

interface OriginalVideosProps {
  slug: string;
  name: string;
}

// Browser webm-playback capability — a BROWSER property, computed ONCE at module
// load, never per-clip. Safari/iOS returns "" for webm (can't play it); Chrome/
// Firefox return "maybe"/"probably". This drives the ladder's webm tier so a
// webm-captured answer falls back gracefully where the browser can't decode it.
const CAN_PLAY_WEBM =
  typeof document !== "undefined" &&
  document.createElement("video").canPlayType('video/webm; codecs="vp8, opus"') !== "";

const isPresent = (s: string | null | undefined): s is string =>
  typeof s === "string" && s.trim() !== "";

type ResolvedClip = {
  question_id: string;
  question_text: string | null;
  playable: boolean;
  src: string | null;
};

// Per-clip playback decision. Preference ladder, in order:
//   1. MP4 derivative present            -> play it (broadest compatibility).
//   2. original is mp4                   -> play the original.
//   3. original is webm AND browser can play webm -> play the original.
//   4. otherwise                         -> not playable (friendly fallback).
const resolveClip = (clip: VideoClip): ResolvedClip => {
  const base = { question_id: clip.question_id, question_text: clip.question_text };
  if (isPresent(clip.video_mp4_url)) {
    return { ...base, playable: true, src: clip.video_mp4_url };
  }
  if (isPresent(clip.video_url) && clip.video_content_type === "video/mp4") {
    return { ...base, playable: true, src: clip.video_url };
  }
  if (
    isPresent(clip.video_url) &&
    clip.video_content_type === "video/webm" &&
    CAN_PLAY_WEBM
  ) {
    return { ...base, playable: true, src: clip.video_url };
  }
  return { ...base, playable: false, src: null };
};

// Public per-question raw-VIDEO player (Slice 2). Mirrors OriginalRecordings: it
// is the SOLE arbiter of its own visibility — TributePage renders it
// unconditionally and stays ignorant. Renders nothing unless the
// tribute-raw-video seam returns enabled with clips, so it is invisible whenever
// the VIDEO release flag is off (the common case today).
const OriginalVideos = ({ slug, name }: OriginalVideosProps) => {
  const [state, setState] = useState<State>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    supabase.functions
      .invoke<VideoSeamResponse>("tribute-raw-video", { body: { slug } })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          setState({ status: "error" });
          return;
        }
        setState({
          status: "ready",
          enabled: !!data.enabled,
          clips: data.clips ?? [],
        });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Resolve every clip ONCE per data change (hook runs unconditionally, before
  // the self-gating returns below).
  const resolved = useMemo(
    () => (state.status === "ready" ? state.clips.map(resolveClip) : []),
    [state],
  );

  // Render nothing in every case except a successful, enabled response with clips
  // — identical self-gating to OriginalRecordings. No skeleton on loading, no
  // retry on error (v1).
  if (state.status !== "ready") return null; // loading or error -> absent
  if (!state.enabled) return null;
  if (resolved.length === 0) return null;

  return (
    <section className="mb-12">
      <h2 className="font-display text-2xl font-semibold text-foreground text-center mb-1">
        On Camera
      </h2>
      <p className="text-center text-muted-foreground text-sm mb-6">
        {`${name}'s original video answers, unedited.`}
      </p>
      <div className="space-y-6">
        {resolved.map((clip) => (
          <div
            key={clip.question_id}
            className="rounded-xl border border-border/30 bg-card p-4"
          >
            {clip.question_text && (
              <p className="text-foreground/90 text-sm leading-relaxed mb-3">
                {clip.question_text}
              </p>
            )}
            {clip.playable && clip.src ? (
              <video
                controls
                playsInline
                preload="none"
                src={clip.src}
                className="w-full rounded-lg bg-black"
              >
                Your browser does not support video playback.
              </video>
            ) : (
              <p className="text-sm text-muted-foreground">
                This video isn&apos;t playable in your browser yet.
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default OriginalVideos;
